'use client'
import { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'
import 'swiper/css'
import 'swiper/css/navigation'
import LinkComponent    from '@components/LinkComponent'
import LargeProjectCard from '@components/LargeProjectCard'

// Same chevron-in-a-circle nav as PropertyCardSlider / AgentCardSlider
function NavButton({ innerRef, direction, disabled }) {
  const isPrev = direction === 'prev'
  return (
    <button
      ref={innerRef}
      type="button"
      aria-label={isPrev ? 'Previous' : 'Next'}
      disabled={disabled}
      className={`absolute top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-1.5 disabled:opacity-0 disabled:pointer-events-none transition-all ${isPrev ? 'left-2' : 'right-2'}`}
    >
      {isPrev ? <MdChevronLeft size={22} /> : <MdChevronRight size={22} />}
    </button>
  )
}

const ProjectCardSlider = ({
  title, description, projectSlider, projects, url, cardStyle, dictionary, lang,
  // Per-section title/subtitle styling — layered on top of the
  // carousel-title-align / carousel-description-align classes
  titleColor, titleSize, titleWeight, titleAlign, subtitleColor, subtitleSize,
  // Section background: bgType 'solid' | 'gradient' | 'image' (+ optional overlay)
  bgType, bgColor, bgGradient, bgImageUrl, overlayColor, overlayOpacity,
}) => {

  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const hasTitle = !!title?.[lang]?.trim()
  const hasDesc  = !!description?.[lang]?.trim()

  const titleStyle = {
    color:      titleColor  || undefined,
    fontSize:   titleSize   || undefined,
    fontWeight: titleWeight || undefined,
    textAlign:  titleAlign  || undefined,
  }
  const descStyle = {
    color:     subtitleColor || undefined,
    fontSize:  subtitleSize  || undefined,
    textAlign: titleAlign    || undefined,
  }

  const resolvedBg = bgType === 'gradient' ? (bgGradient || undefined)
    : bgType === 'image' && bgImageUrl
      ? `url(${bgImageUrl}) center/cover no-repeat`
      : (bgColor || undefined)
  const showOverlay = bgType === 'image' && bgImageUrl && overlayOpacity > 0
  const overlayEl = showOverlay ? (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      background: overlayColor || '#000000', opacity: overlayOpacity,
    }} />
  ) : null

  const header = (<>
    {hasTitle && (url ? (
      <LinkComponent href={url}>
        <h2 className='carousel-title-align' style={titleStyle}>{title[lang]}</h2>
      </LinkComponent>
    ) : (
      <h2 className='carousel-title-align' style={titleStyle}>{title[lang]}</h2>
    ))}
    {hasDesc && (
      <p className='carousel-description-align' style={descStyle}>{description[lang]}</p>
    )}
  </>)

  const ps = projectSlider?.projectSlider ?? {}

  if (!mounted) {
    const maxSlides = ps.desktop || ps.tablet || Math.ceil(ps.mobile || 1.2) || 3
    const limited = projects.slice(0, maxSlides)
    return (
      <section className='carousel-wrapper lg:pl-0 pl-3' style={{ position: 'relative', background: resolvedBg }}>
        {overlayEl}
        <div style={{ position: 'relative' }}>
          {header}
          <div className="flex flex-row" style={{ gap: '16px' }}>
            {limited.map((project, i) => (
              <LargeProjectCard key={i} project={project} cardStyle={cardStyle} dictionary={dictionary?.card} lang={lang} />
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className='carousel-wrapper w-full lg:pl-0 pl-3' style={{ position: 'relative', background: resolvedBg }}>
      {overlayEl}
      <div style={{ position: 'relative' }}>
        {header}
        <div className="relative">
          <Swiper
            spaceBetween={16}
            modules={[Navigation]}
            slidesPerView={ps.mobile || 1.2}
            breakpoints={{
              768:  { slidesPerView: ps.tablet  || 2 },
              1024: { slidesPerView: ps.desktop || 3 },
            }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current
              swiper.params.navigation.nextEl = nextRef.current
            }}
            navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
            onSlideChange={(swiper) => { setIsBeginning(swiper.isBeginning); setIsEnd(swiper.isEnd) }}
            onSwiper={(swiper) => { setIsBeginning(swiper.isBeginning); setIsEnd(swiper.isEnd) }}
          >
            {projects.map((project, i) => (
              <SwiperSlide key={i}>
                <LargeProjectCard project={project} cardStyle={cardStyle} dictionary={dictionary?.card} lang={lang} />
              </SwiperSlide>
            ))}
          </Swiper>
          <NavButton innerRef={prevRef} direction="prev" disabled={isBeginning} />
          <NavButton innerRef={nextRef} direction="next" disabled={isEnd} />
        </div>
      </div>
    </section>
  )
}

export default ProjectCardSlider